// ═══════════════════════════════════════════════════════════════════
//  WEDOO INTERIORS — CRM LEAD MANAGEMENT ADMIN PAGES
//  AdminLeads.js | AdminLeadDetail.js | AdminPipeline.js
//  AdminLeadAnalytics.js | AdminQuotations.js | AdminReminders.js
// ═══════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { sanitizePhone, isValidPhone, isValidOptionalPhone, PHONE_ERROR, PHONE_PATTERN, phoneInputProps } from '../utils/phone';

// ── Shared constants ────────────────────────────────────────────────
const STAGES = ['New Enquiry','Site Visit Scheduled','Design Discussion','Quotation Sent','Negotiation','Project Confirmed','On Hold','Lost'];
const PRIORITIES = ['Hot','Warm','Cold'];
const SOURCES = ['Website Form','WhatsApp','Instagram','Facebook','Google Ads','JustDial','Referral','Walk-in','Phone Call','Property Portal','Other'];
const SERVICES = ['Interior Design','Modular Kitchen','False Ceiling & Painting','Home Lighting','Aluminium & Glass Works','Complete Modular Works'];
const PROPERTY_TYPES = ['1BHK','2BHK','3BHK','4BHK','Villa','Duplex','Commercial','Office','Retail','Other'];
const TIMELINES = ['Immediate','1-3 months','3-6 months','6+ months','Just exploring'];
const ACTIVITY_TYPES = ['call','whatsapp','email','meeting','site_visit','note','document','quotation'];
const BUDGETS = ['Under ₹5 Lakhs','₹5–10 Lakhs','₹10–20 Lakhs','₹20–50 Lakhs','₹50 Lakhs+'];

const STAGE_COLORS = {
  'New Enquiry':           { bg:'rgba(245,168,0,0.12)', color:'#F5A800', border:'rgba(245,168,0,0.3)' },
  'Site Visit Scheduled':  { bg:'rgba(96,165,250,0.1)',  color:'#60a5fa', border:'rgba(96,165,250,0.25)' },
  'Design Discussion':     { bg:'rgba(167,139,250,0.1)', color:'#a78bfa', border:'rgba(167,139,250,0.25)' },
  'Quotation Sent':        { bg:'rgba(251,191,36,0.1)',  color:'#fbbf24', border:'rgba(251,191,36,0.25)' },
  'Negotiation':           { bg:'rgba(251,146,60,0.1)',  color:'#fb923c', border:'rgba(251,146,60,0.25)' },
  'Project Confirmed':     { bg:'rgba(74,222,128,0.1)',  color:'#4ade80', border:'rgba(74,222,128,0.25)' },
  'On Hold':               { bg:'rgba(148,163,184,0.1)', color:'#94a3b8', border:'rgba(148,163,184,0.25)' },
  'Lost':                  { bg:'rgba(248,113,113,0.1)', color:'#f87171', border:'rgba(248,113,113,0.25)' },
};
const PRIORITY_COLORS = { Hot:'#f87171', Warm:'#fbbf24', Cold:'#60a5fa' };
const ScoreBar = ({ score }) => (
  <div style={{ display:'flex', alignItems:'center', gap:'0.5rem' }}>
    <div style={{ flex:1, height:'4px', background:'var(--ink-5)', borderRadius:'2px', overflow:'hidden' }}>
      <div style={{ height:'100%', width:`${score}%`, background: score>=70?'#4ade80':score>=40?'#fbbf24':'#f87171', borderRadius:'2px', transition:'width 0.5s' }} />
    </div>
    <span style={{ fontSize:'0.7rem', color:'var(--muted)', minWidth:'26px' }}>{score}</span>
  </div>
);

// Input component
const FI = ({ label, ...props }) => (
  <div className="form-group">
    {label && <label>{label}</label>}
    <input {...props} />
  </div>
);

// ═══════════════════════════════════════════════════════════════════
//  ADMIN LEADS — main list view
// ═══════════════════════════════════════════════════════════════════
export function AdminLeads() {
  const [leads, setLeads]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [filters, setFilters] = useState({ stage:'', priority:'', source:'', assignedTo:'' });
  const [page, setPage]       = useState(1);
  const [showAdd, setShowAdd] = useState(false);
  const [users, setUsers]     = useState([]);
  const navigate = useNavigate();

  const loadLeads = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 30, sort: '-createdAt', ...filters });
      if (search) params.set('search', search);
      Object.keys(filters).forEach(k => { if (!filters[k]) params.delete(k); });
      const { data } = await api.get(`/leads?${params}`);
      setLeads(Array.isArray(data.leads)?data.leads:[]); setTotal(data.total||0);
    } catch { toast.error('Failed to load leads'); }
    finally { setLoading(false); }
  }, [page, search, filters]);

  useEffect(() => { loadLeads(); }, [loadLeads]);
  useEffect(() => { api.get('/auth/users').then(r => setUsers(Array.isArray(r.data)?r.data:(r.data?.data||[]))).catch(() => {}); }, []);

  const quickUpdateStage = async (id, stage) => {
    try {
      await api.patch(`/leads/${id}`, { stage });
      toast.success('Stage updated');
      loadLeads();
    } catch { toast.error('Failed'); }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <p className="tag-label" style={{ marginBottom:'0.3rem' }}>CRM</p>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'1.8rem', color:'var(--cream)' }}>Lead Management</h1>
        </div>
        <div style={{ display:'flex', gap:'0.75rem' }}>
          <Link to="/admin/crm/pipeline" className="btn btn-dark" style={{ padding:'0.6rem 1.25rem', fontSize:'0.72rem' }}>📋 Pipeline View</Link>
          <Link to="/admin/crm/analytics" className="btn btn-dark" style={{ padding:'0.6rem 1.25rem', fontSize:'0.72rem' }}>📊 Analytics</Link>
          <Link to="/admin/crm/reminders" className="btn btn-dark" style={{ padding:'0.6rem 1.25rem', fontSize:'0.72rem' }}>🔔 Reminders</Link>
          <button className="btn btn-gold" style={{ padding:'0.6rem 1.25rem', fontSize:'0.72rem' }} onClick={() => setShowAdd(true)}>+ Add Lead</button>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:'0.75rem', marginBottom:'1.25rem', flexWrap:'wrap', alignItems:'center' }}>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
          placeholder="Search name, phone, location..." style={{ flex:'1', minWidth:'200px', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', color:'var(--cream)', padding:'0.6rem 1rem', fontSize:'0.82rem' }} />
        {[
          ['stage', ['', ...STAGES]],
          ['priority', ['', ...PRIORITIES]],
          ['source', ['', ...SOURCES]],
        ].map(([key, opts]) => (
          <select key={key} value={filters[key]} onChange={e => { setFilters(p => ({ ...p, [key]: e.target.value })); setPage(1); }}
            style={{ background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', color:'var(--cream)', padding:'0.6rem 0.85rem', fontSize:'0.78rem' }}>
            <option value="">All {key.charAt(0).toUpperCase()+key.slice(1)}s</option>
            {opts.filter(Boolean).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        ))}
        <span style={{ color:'var(--muted)', fontSize:'0.78rem', whiteSpace:'nowrap' }}>{total} leads</span>
      </div>

      {/* Lead table */}
      {loading ? (
        <div className="admin-empty">Loading leads...</div>
      ) : leads.length === 0 ? (
        <div style={{ textAlign:'center', padding:'5rem' }}>
          <div style={{ fontSize:'3rem', marginBottom:'1.5rem' }}>📋</div>
          <h3 style={{ fontFamily:'var(--font-serif)', color:'var(--cream)', marginBottom:'0.75rem' }}>No Leads Yet</h3>
          <p style={{ color:'var(--muted)', marginBottom:'2rem', fontSize:'0.88rem' }}>Add your first lead manually or capture leads from website forms.</p>
          <button className="btn btn-gold" onClick={() => setShowAdd(true)}>+ Add First Lead</button>
        </div>
      ) : (
        <div style={{ overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.82rem' }}>
            <thead>
              <tr style={{ borderBottom:'1px solid var(--ink-5)' }}>
                {['#','Lead','Contact','Property','Budget','Source','Stage','Priority','Score','Next Follow-up',''].map(h => (
                  <th key={h} style={{ padding:'0.65rem 0.75rem', textAlign:'left', fontSize:'0.65rem', letterSpacing:'0.12em', textTransform:'uppercase', color:'var(--muted)', whiteSpace:'nowrap', fontWeight:'500' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => (
                <tr key={lead._id} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)', transition:'background 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background='rgba(255,255,255,0.02)'}
                  onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                  <td style={{ padding:'0.75rem', color:'var(--muted)', fontSize:'0.7rem' }}>#{lead.leadNumber}</td>
                  <td style={{ padding:'0.75rem', minWidth:'140px' }}>
                    <button onClick={() => navigate(`/admin/crm/leads/${lead._id}`)} style={{ textAlign:'left', background:'none', cursor:'pointer' }}>
                      <div style={{ fontWeight:'500', color:'var(--cream)', marginBottom:'2px' }}>{lead.name}</div>
                      {lead.location && <div style={{ fontSize:'0.72rem', color:'var(--muted)' }}>📍 {lead.location}</div>}
                    </button>
                  </td>
                  <td style={{ padding:'0.75rem' }}>
                    <a href={`tel:${lead.phone}`} style={{ color:'var(--gold)', fontSize:'0.8rem', display:'block' }}>{lead.phone}</a>
                    {lead.email && <span style={{ fontSize:'0.7rem', color:'var(--muted)' }}>{lead.email}</span>}
                  </td>
                  <td style={{ padding:'0.75rem' }}>
                    <div style={{ color:'var(--cream-2)', fontSize:'0.8rem' }}>{lead.propertyType || lead.bhkType || '—'}</div>
                    {lead.projectArea && <div style={{ fontSize:'0.7rem', color:'var(--muted)' }}>{lead.projectArea} sq ft</div>}
                  </td>
                  <td style={{ padding:'0.75rem', color:'var(--cream-2)', fontSize:'0.8rem', whiteSpace:'nowrap' }}>{lead.budget || '—'}</td>
                  <td style={{ padding:'0.75rem' }}>
                    <span style={{ fontSize:'0.72rem', color:'var(--muted)' }}>{lead.source}</span>
                  </td>
                  <td style={{ padding:'0.75rem' }}>
                    <select value={lead.stage} onChange={e => quickUpdateStage(lead._id, e.target.value)}
                      style={{ background:'transparent', border:'none', cursor:'pointer', fontSize:'0.72rem', color:STAGE_COLORS[lead.stage]?.color||'var(--cream)', padding:'0' }}>
                      {STAGES.map(s => <option key={s} value={s} style={{ background:'var(--ink-3)', color:'var(--cream)' }}>{s}</option>)}
                    </select>
                  </td>
                  <td style={{ padding:'0.75rem' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem' }}>
                      <PriorityDot priority={lead.priority} />
                      <span style={{ fontSize:'0.72rem', color:'var(--muted)' }}>{lead.priority}</span>
                    </div>
                  </td>
                  <td style={{ padding:'0.75rem', minWidth:'80px' }}><ScoreBar score={lead.score||0} /></td>
                  <td style={{ padding:'0.75rem', fontSize:'0.72rem', color:'var(--muted)', whiteSpace:'nowrap' }}>
                    {lead.nextFollowUp ? (
                      <span style={{ color: new Date(lead.nextFollowUp) < new Date() ? '#f87171' : 'var(--muted)' }}>
                        {new Date(lead.nextFollowUp).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                      </span>
                    ) : '—'}
                  </td>
                  <td style={{ padding:'0.75rem' }}>
                    <Link to={`/admin/crm/leads/${lead._id}`} style={{ color:'var(--gold)', fontSize:'0.72rem', letterSpacing:'0.08em', whiteSpace:'nowrap' }}>View →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {/* Pagination */}
          {total > 30 && (
            <div style={{ display:'flex', gap:'0.5rem', justifyContent:'center', marginTop:'1.5rem' }}>
              {Array.from({ length: Math.ceil(total/30) }, (_, i) => (
                <button key={i} onClick={() => setPage(i+1)} style={{
                  width:'34px', height:'34px', fontSize:'0.78rem', cursor:'pointer',
                  background: page===i+1 ? 'var(--gold)' : 'var(--ink-3)',
                  color: page===i+1 ? 'var(--ink)' : 'var(--muted)',
                  border:'1px solid var(--ink-5)', borderRadius:'var(--radius)',
                }}>{i+1}</button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add Lead Modal */}
      {showAdd && <AddLeadModal users={users} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadLeads(); }} />}
    </div>
  );
}

// ─── Add Lead Modal ───────────────────────────────────────────────
function AddLeadModal({ users, onClose, onSaved }) {
  const [form, setForm] = useState({ name:'', phone:'', email:'', whatsapp:'', location:'Tumakuru', budget:'', propertyType:'', timeline:'', source:'Phone Call', priority:'Warm', services:[], notes:'' });
  const [saving, setSaving] = useState(false);
  const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));
  const fPhone = k => e => setForm(p => ({ ...p, [k]: sanitizePhone(e.target.value) }));
  const toggleService = s => setForm(p => ({ ...p, services: p.services.includes(s) ? p.services.filter(x=>x!==s) : [...p.services, s] }));

  const submit = async e => {
    e.preventDefault();
    if (!isValidPhone(form.phone)) { toast.error(PHONE_ERROR); return; }
    if (!isValidOptionalPhone(form.whatsapp)) { toast.error(`WhatsApp: ${PHONE_ERROR}`); return; }
    setSaving(true);
    try { await api.post('/leads', form); toast.success('Lead added!'); onSaved(); }
    catch { toast.error('Failed to add lead'); } finally { setSaving(false); }
  };

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:'1rem' }}>
      <div style={{ background:'var(--ink-2)', border:'1px solid rgba(245,168,0,0.2)', width:'100%', maxWidth:'640px', maxHeight:'90vh', overflowY:'auto', borderRadius:'2px' }}>
        <div style={{ padding:'1.5rem', borderBottom:'1px solid var(--ink-4)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.3rem', color:'var(--cream)' }}>Add New Lead</h3>
          <button onClick={onClose} style={{ color:'var(--muted)', fontSize:'1.3rem' }}>✕</button>
        </div>
        <form onSubmit={submit} style={{ padding:'1.5rem' }}>
          <div className="grid-2" style={{ gap:'1rem' }}>
            <FI label="Full Name *" value={form.name} onChange={f('name')} required placeholder="Client name" />
            <FI label="Phone *" value={form.phone} onChange={fPhone('phone')} required placeholder="10-digit mobile number" pattern={PHONE_PATTERN} title={PHONE_ERROR} {...phoneInputProps} />
            <FI label="Email" value={form.email} onChange={f('email')} placeholder="email@example.com" type="email" />
            <FI label="WhatsApp" value={form.whatsapp} onChange={fPhone('whatsapp')} placeholder="10-digit mobile number" {...phoneInputProps} />
            <FI label="Location / City" value={form.location} onChange={f('location')} placeholder="Tumakuru" />
            <div className="form-group">
              <label>Budget Range</label>
              <select value={form.budget} onChange={f('budget')} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                <option value="">Select budget</option>
                {BUDGETS.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Property Type</label>
              <select value={form.propertyType} onChange={f('propertyType')} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                <option value="">Select type</option>
                {PROPERTY_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Timeline</label>
              <select value={form.timeline} onChange={f('timeline')} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                <option value="">Select timeline</option>
                {TIMELINES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Source</label>
              <select value={form.source} onChange={f('source')} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                {SOURCES.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Priority</label>
              <select value={form.priority} onChange={f('priority')} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
          <div className="form-group" style={{ marginTop:'0.5rem' }}>
            <label>Services Interested In</label>
            <div className="grid-2" style={{ gap:'0.4rem', marginTop:'0.5rem' }}>
              {SERVICES.map(s => (
                <label key={s} style={{ display:'flex', alignItems:'center', gap:'0.5rem', cursor:'pointer', fontSize:'0.8rem', color: form.services.includes(s)?'var(--gold)':'var(--muted)', padding:'0.4rem', border:`1px solid ${form.services.includes(s)?'rgba(245,168,0,0.4)':'var(--ink-5)'}`, background: form.services.includes(s)?'rgba(245,168,0,0.08)':'transparent' }}>
                  <input type="checkbox" checked={form.services.includes(s)} onChange={() => toggleService(s)} style={{ accentColor:'#F5A800' }} />
                  {s}
                </label>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label>Notes</label>
            <textarea value={form.notes} onChange={f('notes')} rows={3} placeholder="Any additional information about this lead..." style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem', resize:'vertical' }} />
          </div>
          <div style={{ display:'flex', gap:'0.75rem', paddingTop:'0.5rem' }}>
            <button type="submit" className="btn btn-gold" style={{ flex:1, justifyContent:'center' }} disabled={saving}>{saving?'Saving...':'✦ Add Lead'}</button>
            <button type="button" className="btn btn-dark" onClick={onClose}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  LEAD DETAIL — full CRM record
// ═══════════════════════════════════════════════════════════════════
export function AdminLeadDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]         = useState('overview');
  const [users, setUsers]     = useState([]);
  const [saving, setSaving]   = useState(false);
  const [actForm, setActForm] = useState({ type:'call', title:'', notes:'', outcome:'', duration:'', nextAction:'' });
  const [remForm, setRemForm] = useState({ type:'call', message:'', scheduledAt:'' });
  const [editLead, setEditLead] = useState(false);
  const [leadEdit, setLeadEdit] = useState({});

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get(`/leads/${id}`),
      api.get('/auth/users'),
    ]).then(([leadRes, usersRes]) => {
      setData(leadRes.data);
      setLeadEdit(leadRes.data.lead);
      setUsers(usersRes.data);
    }).catch(() => toast.error('Failed to load'))
    .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const updateLead = async (patch) => {
    try { await api.patch(`/leads/${id}`, patch); load(); toast.success('Updated!'); }
    catch { toast.error('Failed'); }
  };

  const addActivity = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/activities', { ...actForm, lead: id });
      toast.success('Activity logged!');
      setActForm({ type:'call', title:'', notes:'', outcome:'', duration:'', nextAction:'' });
      load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const addReminder = async e => {
    e.preventDefault(); setSaving(true);
    try {
      await api.post('/reminders', { ...remForm, lead: id, assignedTo: data?.lead?.assignedTo?._id || user._id });
      toast.success('Reminder set!');
      setRemForm({ type:'call', message:'', scheduledAt:'' });
      load();
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const markReminderDone = async (remId) => {
    try { await api.patch(`/reminders/${remId}`, { isDone: true }); load(); toast.success('Marked done'); }
    catch { toast.error('Failed'); }
  };

  const deleteActivity = async (actId) => {
    if (!window.confirm('Delete this activity?')) return;
    try { await api.delete(`/activities/${actId}`); load(); toast.success('Deleted'); }
    catch { toast.error('Failed'); }
  };

  const activityIcon = (type) => ({ call:'📞', whatsapp:'💬', email:'✉', meeting:'🤝', site_visit:'🏠', note:'📝', document:'📄', quotation:'📋', stage_change:'🔄', assignment:'👤', reminder:'🔔' })[type] || '•';

  if (loading) return <div className="admin-empty">Loading lead...</div>;
  if (!data?.lead) return <div style={{ textAlign:'center', padding:'4rem' }}><button className="btn btn-dark" onClick={() => navigate(-1)}>← Back</button><p style={{ color:'var(--muted)', marginTop:'1rem' }}>Lead not found</p></div>;

  const { lead, activities, reminders, quotations } = data;

  return (
    <div>
      {/* Breadcrumb + actions */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <button onClick={() => navigate('/admin/crm/leads')} style={{ color:'var(--muted)', fontSize:'0.78rem', letterSpacing:'0.1em', textTransform:'uppercase', background:'none', cursor:'pointer', marginBottom:'0.5rem' }}>← Back to Leads</button>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'1.6rem', color:'var(--cream)', display:'flex', alignItems:'center', gap:'0.75rem' }}>
            <PriorityDot priority={lead.priority} />
            {lead.name}
            <span style={{ fontFamily:'var(--font-sans)', fontSize:'0.8rem', color:'var(--muted)' }}>#{lead.leadNumber}</span>
          </h1>
        </div>
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <a href={`tel:${lead.phone}`} className="btn btn-dark" style={{ padding:'0.55rem 1rem', fontSize:'0.7rem' }}>📞 Call</a>
          <a href={`https://api.whatsapp.com/send?phone=91${lead.phone?.replace(/\D/g,'')}&text=Hi ${encodeURIComponent(lead.name)}, this is from Wedoo Interiors regarding your interior design enquiry.`} target="_blank" rel="noopener noreferrer" className="btn btn-dark" style={{ padding:'0.55rem 1rem', fontSize:'0.7rem' }}>💬 WhatsApp</a>
          {lead.email && <a href={`mailto:${lead.email}`} className="btn btn-dark" style={{ padding:'0.55rem 1rem', fontSize:'0.7rem' }}>✉ Email</a>}
          <Link to={`/admin/crm/quotations/new/${lead._id}`} className="btn btn-gold" style={{ padding:'0.55rem 1rem', fontSize:'0.7rem' }}>📋 New Quote</Link>
        </div>
      </div>

      {/* Quick status bar */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:'0.75rem', marginBottom:'1.5rem' }}>
        {[
          { label:'Stage', value:<StageBadge stage={lead.stage} /> },
          { label:'Priority', value:<span style={{ color:PRIORITY_COLORS[lead.priority], fontWeight:'500' }}>{lead.priority}</span> },
          { label:'Source', value:<span style={{ color:'var(--cream-2)', fontSize:'0.82rem' }}>{lead.source}</span> },
          { label:'Score', value:<ScoreBar score={lead.score||0} /> },
          { label:'Lead Score', value:<span style={{ fontFamily:'var(--font-serif)', color:'var(--gold)', fontSize:'1.2rem' }}>{lead.score}/100</span> },
        ].map(s => (
          <div key={s.label} style={{ background:'var(--ink-2)', border:'1px solid var(--ink-4)', borderRadius:'var(--radius)', padding:'0.85rem 1rem' }}>
            <div style={{ fontSize:'0.62rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.4rem' }}>{s.label}</div>
            <div>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:'0', borderBottom:'1px solid var(--ink-4)', marginBottom:'1.75rem', overflowX:'auto' }}>
        {[['overview','Overview'],['activity','Activity Log'],['reminders','Reminders'],['quotations','Quotations']].map(([key,label]) => (
          <button key={key} onClick={() => setTab(key)} style={{
            padding:'0.75rem 1.25rem', fontSize:'0.75rem', fontWeight:'500',
            letterSpacing:'0.08em', textTransform:'uppercase', cursor:'pointer', whiteSpace:'nowrap',
            background:'none', borderBottom: tab===key?'2px solid var(--gold)':'2px solid transparent',
            color: tab===key?'var(--gold)':'var(--muted)', transition:'all 0.2s',
          }}>{label}{key==='reminders'&&reminders?.filter(r=>!r.isDone).length?<span style={{ marginLeft:'0.4rem', background:'#f87171', color:'white', fontSize:'0.6rem', padding:'1px 5px', borderRadius:'10px' }}>{reminders.filter(r=>!r.isDone).length}</span>:null}</button>
        ))}
      </div>

      {/* Tab: Overview */}
      {tab === 'overview' && (
        <div className="crm-detail-grid" style={{ gap:'1.5rem' }}>
          {/* Lead details */}
          <div className="card" style={{ padding:'1.5rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'1.25rem' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.1rem', color:'var(--cream)' }}>Lead Details</h3>
              <button className="btn btn-dark" style={{ padding:'0.35rem 0.75rem', fontSize:'0.7rem' }} onClick={() => setEditLead(!editLead)}>
                {editLead ? 'Cancel' : '✎ Edit'}
              </button>
            </div>
            {editLead ? (
              <form onSubmit={async e => { e.preventDefault(); await updateLead(leadEdit); setEditLead(false); }}>
                <div className="grid-2" style={{ gap:'0.75rem' }}>
                  {[['name','Name'],['phone','Phone'],['email','Email'],['whatsapp','WhatsApp'],['location','Location'],['city','City'],['projectArea','Area (sq ft)']].map(([k,l]) => (
                    <div key={k} className="form-group" style={{ marginBottom:'0.75rem' }}>
                      <label>{l}</label>
                      <input value={leadEdit[k]||''} onChange={e => setLeadEdit(p=>({...p,[k]:e.target.value}))} />
                    </div>
                  ))}
                </div>
                <button type="submit" className="btn btn-gold" style={{ width:'100%', justifyContent:'center' }}>Save Changes</button>
              </form>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem' }}>
                {[
                  ['📞 Phone', <a href={`tel:${lead.phone}`} style={{ color:'var(--gold)' }}>{lead.phone}</a>],
                  ['💬 WhatsApp', lead.whatsapp || lead.phone],
                  ['✉ Email', lead.email || '—'],
                  ['📍 Location', lead.location || '—'],
                  ['🏠 Property', `${lead.propertyType||''} ${lead.bhkType||''}`||'—'],
                  ['📐 Area', lead.projectArea ? `${lead.projectArea} sq ft` : '—'],
                  ['💰 Budget', lead.budget || '—'],
                  ['⏱ Timeline', lead.timeline || '—'],
                  ['📅 Created', new Date(lead.createdAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })],
                  ['🕐 Last Contact', lead.lastContactedAt ? new Date(lead.lastContactedAt).toLocaleDateString('en-IN') : 'Not contacted'],
                ].map(([label, val]) => (
                  <div key={label} style={{ display:'flex', gap:'0.75rem', paddingBottom:'0.65rem', borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    <span style={{ fontSize:'0.72rem', color:'var(--muted)', minWidth:'100px' }}>{label}</span>
                    <span style={{ fontSize:'0.82rem', color:'var(--cream-2)', flex:1 }}>{val}</span>
                  </div>
                ))}
                {lead.services?.length > 0 && (
                  <div>
                    <span style={{ fontSize:'0.7rem', color:'var(--muted)' }}>Services</span>
                    <div style={{ display:'flex', gap:'0.4rem', flexWrap:'wrap', marginTop:'0.4rem' }}>
                      {lead.services.map(s => <span key={s} className="badge badge-gold" style={{ fontSize:'0.62rem' }}>{s}</span>)}
                    </div>
                  </div>
                )}
                {lead.notes && (
                  <div style={{ background:'var(--ink-3)', padding:'0.85rem', marginTop:'0.5rem' }}>
                    <div style={{ fontSize:'0.65rem', color:'var(--muted)', marginBottom:'0.4rem', textTransform:'uppercase', letterSpacing:'0.12em' }}>Notes</div>
                    <p style={{ color:'var(--cream-2)', fontSize:'0.82rem', lineHeight:'1.7' }}>{lead.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stage + Assignment */}
          <div style={{ display:'flex', flexDirection:'column', gap:'1rem' }}>
            <div className="card" style={{ padding:'1.5rem' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.1rem', color:'var(--cream)', marginBottom:'1rem' }}>Update Stage</h3>
              <div style={{ display:'flex', flexDirection:'column', gap:'0.4rem' }}>
                {STAGES.map(stage => (
                  <button key={stage} onClick={() => updateLead({ stage })} style={{
                    padding:'0.6rem 1rem', textAlign:'left', fontSize:'0.78rem', cursor:'pointer',
                    background: lead.stage===stage ? STAGE_COLORS[stage]?.bg : 'var(--ink-3)',
                    color:      lead.stage===stage ? STAGE_COLORS[stage]?.color : 'var(--muted)',
                    border:     lead.stage===stage ? `1px solid ${STAGE_COLORS[stage]?.border}` : '1px solid var(--ink-5)',
                    fontWeight: lead.stage===stage ? '600' : '400',
                    transition: 'all 0.2s',
                  }}>
                    {lead.stage===stage ? '✓ ' : ''}{stage}
                  </button>
                ))}
              </div>
            </div>
            <div className="card" style={{ padding:'1.5rem' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.1rem', color:'var(--cream)', marginBottom:'1rem' }}>Assignment</h3>
              <div className="form-group" style={{ marginBottom:'0.75rem' }}>
                <label>Assigned To</label>
                <select value={lead.assignedTo?._id||''} onChange={e => {
                  const u = users.find(u => u._id===e.target.value);
                  updateLead({ assignedTo: e.target.value, assignedName: u?.name });
                }} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                  <option value="">Unassigned</option>
                  {users.map(u => <option key={u._id} value={u._id}>{u.name}</option>)}
                </select>
              </div>
              <div className="form-group" style={{ marginBottom:'0.75rem' }}>
                <label>Priority</label>
                <select value={lead.priority} onChange={e => updateLead({ priority: e.target.value })} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              {lead.stage === 'Project Confirmed' && (
                <div className="form-group">
                  <label>Project Value (₹)</label>
                  <input type="number" defaultValue={lead.projectValue||''} onBlur={e => updateLead({ projectValue: parseInt(e.target.value) })} placeholder="Total project value in ₹" style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }} />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Tab: Activity Log */}
      {tab === 'activity' && (
        <div className="crm-detail-grid" style={{ gap:'1.5rem' }}>
          {/* Activity timeline */}
          <div>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Activity Timeline</h3>
            {activities?.length === 0 ? (
              <div className="admin-empty is-outlined">No activities yet. Log the first interaction!</div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:'0' }}>
                {activities?.map((act, i) => (
                  <div key={act._id} style={{ display:'flex', gap:'1rem', paddingBottom:'1.25rem' }}>
                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center' }}>
                      <div style={{ width:'36px', height:'36px', borderRadius:'50%', background:'var(--ink-3)', border:'1px solid var(--ink-4)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'1rem', flexShrink:0 }}>
                        {activityIcon(act.type)}
                      </div>
                      {i < activities.length-1 && <div style={{ width:'1px', flex:1, background:'var(--ink-5)', marginTop:'6px' }} />}
                    </div>
                    <div style={{ flex:1, paddingBottom:'0.5rem' }}>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.3rem' }}>
                        <div>
                          <span style={{ fontWeight:'500', color:'var(--cream)', fontSize:'0.85rem' }}>{act.title || act.type}</span>
                          {act.outcome && <span className="badge badge-gold" style={{ marginLeft:'0.5rem', fontSize:'0.6rem' }}>{act.outcome}</span>}
                        </div>
                        <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                          <span style={{ color:'var(--muted)', fontSize:'0.7rem' }}>
                            {new Date(act.completedAt).toLocaleDateString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                          </span>
                          <button onClick={() => deleteActivity(act._id)} style={{ color:'rgba(248,113,113,0.5)', fontSize:'0.85rem', background:'none', cursor:'pointer' }}>✕</button>
                        </div>
                      </div>
                      {act.notes && <p style={{ color:'var(--muted)', fontSize:'0.8rem', lineHeight:'1.6', margin:'0.25rem 0' }}>{act.notes}</p>}
                      {act.duration && <span style={{ color:'var(--muted)', fontSize:'0.72rem' }}>⏱ {act.duration} min</span>}
                      {act.nextAction && <p style={{ color:'var(--gold)', fontSize:'0.75rem', marginTop:'0.3rem' }}>→ Next: {act.nextAction}</p>}
                      {act.doneName && <p style={{ color:'var(--muted)', fontSize:'0.7rem', marginTop:'0.25rem' }}>by {act.doneName}</p>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Log activity form */}
          <div className="card crm-detail-sidebar" style={{ padding:'1.5rem', height:'fit-content', position:'sticky', top:'6rem' }}>
            <h4 style={{ fontFamily:'var(--font-serif)', fontSize:'1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Log Activity</h4>
            <form onSubmit={addActivity}>
              <div className="form-group">
                <label>Type</label>
                <div className="grid-2" style={{ gap:'0.35rem' }}>
                  {ACTIVITY_TYPES.map(t => (
                    <button type="button" key={t} onClick={() => setActForm(p=>({...p,type:t}))} style={{
                      padding:'0.45rem 0.5rem', fontSize:'0.7rem', cursor:'pointer', textTransform:'capitalize',
                      background: actForm.type===t?'rgba(245,168,0,0.15)':'var(--ink-3)',
                      border: `1px solid ${actForm.type===t?'rgba(245,168,0,0.4)':'var(--ink-5)'}`,
                      color: actForm.type===t?'var(--gold)':'var(--muted)',
                    }}>
                      {activityIcon(t)} {t.replace('_',' ')}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label>Title / Summary</label>
                <input value={actForm.title} onChange={e=>setActForm(p=>({...p,title:e.target.value}))} placeholder="e.g. Called — interested in 3BHK kitchen" required />
              </div>
              {['call','meeting','site_visit'].includes(actForm.type) && (
                <div className="grid-2" style={{ gap:'0.75rem' }}>
                  <div className="form-group">
                    <label>Outcome</label>
                    <select value={actForm.outcome} onChange={e=>setActForm(p=>({...p,outcome:e.target.value}))} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 0.75rem', fontSize:'0.8rem' }}>
                      <option value="">Select...</option>
                      {['Connected','No answer','Busy','Called back','Interested','Not interested','Revisit','Confirmed'].map(o=><option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Duration (min)</label>
                    <input type="number" value={actForm.duration} onChange={e=>setActForm(p=>({...p,duration:e.target.value}))} placeholder="15" />
                  </div>
                </div>
              )}
              <div className="form-group">
                <label>Notes</label>
                <textarea value={actForm.notes} onChange={e=>setActForm(p=>({...p,notes:e.target.value}))} rows={3} placeholder="What was discussed?" style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem', resize:'vertical' }} />
              </div>
              <div className="form-group">
                <label>Next Action</label>
                <input value={actForm.nextAction} onChange={e=>setActForm(p=>({...p,nextAction:e.target.value}))} placeholder="e.g. Send quotation by Friday" />
              </div>
              <button type="submit" className="btn btn-gold" style={{ width:'100%', justifyContent:'center' }} disabled={saving}>
                {saving ? 'Logging...' : '✦ Log Activity'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Tab: Reminders */}
      {tab === 'reminders' && (
        <div className="crm-detail-grid" style={{ gap:'1.5rem' }}>
          <div>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Follow-up Reminders</h3>
            {reminders?.length === 0 ? (
              <div className="admin-empty is-outlined">No reminders set. Add a follow-up to stay on track.</div>
            ) : (
              reminders?.map(rem => (
                <div key={rem._id} className="card" style={{ padding:'1.25rem', marginBottom:'0.75rem', display:'flex', justifyContent:'space-between', alignItems:'flex-start', opacity: rem.isDone?0.5:1 }}>
                  <div>
                    <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.4rem' }}>
                      <span style={{ fontSize:'1rem' }}>{ {'call':'📞','whatsapp':'💬','meeting':'🤝','site_visit':'🏠','follow_up':'🔔'}[rem.type]||'🔔'}</span>
                      <span style={{ fontWeight:'500', color:'var(--cream)', fontSize:'0.88rem', textTransform:'capitalize' }}>{rem.type.replace('_',' ')}</span>
                      {rem.isDone && <span className="badge badge-green" style={{ fontSize:'0.6rem' }}>Done</span>}
                      {!rem.isDone && new Date(rem.scheduledAt) < new Date() && <span className="badge badge-red" style={{ fontSize:'0.6rem' }}>Overdue</span>}
                    </div>
                    {rem.message && <p style={{ color:'var(--muted)', fontSize:'0.82rem', marginBottom:'0.4rem' }}>{rem.message}</p>}
                    <p style={{ color: new Date(rem.scheduledAt)<new Date()&&!rem.isDone?'#f87171':'var(--gold)', fontSize:'0.78rem' }}>
                      📅 {new Date(rem.scheduledAt).toLocaleString('en-IN', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
                    </p>
                  </div>
                  {!rem.isDone && (
                    <button className="btn btn-dark" style={{ padding:'0.4rem 0.85rem', fontSize:'0.7rem', flexShrink:0 }} onClick={() => markReminderDone(rem._id)}>
                      ✓ Done
                    </button>
                  )}
                </div>
              ))
            )}
          </div>
          {/* Add reminder */}
          <div className="card crm-detail-sidebar" style={{ padding:'1.5rem', height:'fit-content', position:'sticky', top:'6rem' }}>
            <h4 style={{ fontFamily:'var(--font-serif)', fontSize:'1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Set Reminder</h4>
            <form onSubmit={addReminder}>
              <div className="form-group">
                <label>Reminder Type</label>
                <select value={remForm.type} onChange={e=>setRemForm(p=>({...p,type:e.target.value}))} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem' }}>
                  {['call','whatsapp','meeting','site_visit','follow_up'].map(t=><option key={t} value={t}>{t.replace('_',' ')}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Date & Time *</label>
                <input type="datetime-local" value={remForm.scheduledAt} onChange={e=>setRemForm(p=>({...p,scheduledAt:e.target.value}))} required />
              </div>
              <div className="form-group">
                <label>Note / Message</label>
                <textarea value={remForm.message} onChange={e=>setRemForm(p=>({...p,message:e.target.value}))} rows={3} placeholder="What to discuss or do?" style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem', resize:'vertical' }} />
              </div>
              <button type="submit" className="btn btn-gold" style={{ width:'100%', justifyContent:'center' }} disabled={saving}>
                {saving ? 'Setting...' : '🔔 Set Reminder'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Tab: Quotations */}
      {tab === 'quotations' && (
        <div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.25rem' }}>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.1rem', color:'var(--cream)' }}>Quotations</h3>
            <Link to={`/admin/crm/quotations/new/${id}`} className="btn btn-gold" style={{ padding:'0.55rem 1.1rem', fontSize:'0.7rem' }}>+ New Quotation</Link>
          </div>
          {quotations?.length === 0 ? (
            <div className="admin-empty is-outlined">
              No quotations yet. <Link to={`/admin/crm/quotations/new/${id}`} style={{ color:'var(--gold)' }}>Create first quotation →</Link>
            </div>
          ) : (
            quotations?.map(q => (
              <div key={q._id} className="card" style={{ padding:'1.25rem', marginBottom:'0.75rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.3rem' }}>
                    <span style={{ fontWeight:'500', color:'var(--cream)', fontSize:'0.9rem' }}>{q.quotationNo}</span>
                    <span className={`badge ${q.status==='Approved'?'badge-green':q.status==='Rejected'?'badge-red':'badge-gold'}`} style={{ fontSize:'0.62rem' }}>{q.status}</span>
                  </div>
                  <p style={{ color:'var(--gold)', fontSize:'0.9rem', fontFamily:'var(--font-serif)' }}>₹{(q.total||0).toLocaleString('en-IN')}</p>
                  <p style={{ color:'var(--muted)', fontSize:'0.72rem', marginTop:'0.25rem' }}>{new Date(q.createdAt).toLocaleDateString('en-IN')}</p>
                </div>
                <Link to={`/admin/crm/quotations/${q._id}`} className="btn btn-dark" style={{ padding:'0.45rem 0.9rem', fontSize:'0.7rem' }}>View →</Link>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  PIPELINE KANBAN VIEW
// ═══════════════════════════════════════════════════════════════════
export function AdminPipeline() {
  const [pipeline, setPipeline] = useState({});
  const [loading, setLoading]   = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/leads/board/pipeline').then(r => setPipeline(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, []);

  const stagesShown = STAGES.filter(s => s !== 'Lost');

  if (loading) return <div className="admin-empty">Loading pipeline...</div>;

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <p className="tag-label" style={{ marginBottom:'0.3rem' }}>Visual Board</p>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'1.8rem', color:'var(--cream)' }}>Sales Pipeline</h1>
        </div>
        <div style={{ display:'flex', gap:'0.5rem', flexWrap:'wrap' }}>
          <Link to="/admin/crm/leads" className="btn btn-dark" style={{ padding:'0.55rem 1rem', fontSize:'0.7rem' }}>📋 List View</Link>
          <Link to="/admin/crm/analytics" className="btn btn-dark" style={{ padding:'0.55rem 1rem', fontSize:'0.7rem' }}>📊 Analytics</Link>
        </div>
      </div>
      <div style={{ display:'flex', gap:'0.75rem', overflowX:'auto', paddingBottom:'1rem', alignItems:'flex-start' }}>
        {stagesShown.map(stage => {
          const leads = pipeline[stage] || [];
          const s = STAGE_COLORS[stage];
          return (
            <div key={stage} style={{ minWidth:'220px', maxWidth:'240px', flexShrink:0 }}>
              {/* Column header */}
              <div style={{ background:s.bg, border:`1px solid ${s.border}`, padding:'0.6rem 0.85rem', marginBottom:'0.6rem', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span style={{ fontSize:'0.7rem', fontWeight:'600', color:s.color, letterSpacing:'0.08em', textTransform:'uppercase' }}>{stage}</span>
                <span style={{ fontSize:'0.7rem', color:s.color, background:s.bg, border:`1px solid ${s.border}`, padding:'1px 7px', borderRadius:'10px' }}>{leads.length}</span>
              </div>
              {/* Cards */}
              <div style={{ display:'flex', flexDirection:'column', gap:'0.5rem', minHeight:'100px' }}>
                {leads.map(lead => (
                  <div key={lead._id} onClick={() => navigate(`/admin/crm/leads/${lead._id}`)} style={{ background:'var(--ink-2)', border:'1px solid var(--ink-4)', borderRadius:'var(--radius)', padding:'0.9rem', cursor:'pointer', transition:'all 0.2s' }}
                    onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(245,168,0,0.3)'; e.currentTarget.style.transform='translateY(-2px)';}}
                    onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--ink-4)'; e.currentTarget.style.transform='none';}}>
                    <div style={{ display:'flex', alignItems:'center', gap:'0.4rem', marginBottom:'0.4rem' }}>
                      <PriorityDot priority={lead.priority} />
                      <span style={{ fontWeight:'500', color:'var(--cream)', fontSize:'0.82rem' }}>{lead.name}</span>
                    </div>
                    {lead.phone && <p style={{ color:'var(--muted)', fontSize:'0.72rem' }}>📞 {lead.phone}</p>}
                    {lead.budget && <p style={{ color:'var(--gold)', fontSize:'0.72rem', marginTop:'0.2rem' }}>💰 {lead.budget}</p>}
                    {lead.propertyType && <p style={{ color:'var(--muted)', fontSize:'0.7rem', marginTop:'0.2rem' }}>🏠 {lead.propertyType}</p>}
                    <div style={{ display:'flex', justifyContent:'space-between', marginTop:'0.5rem', paddingTop:'0.5rem', borderTop:'1px solid rgba(255,255,255,0.05)' }}>
                      <span style={{ fontSize:'0.65rem', color:'var(--muted)' }}>{lead.source}</span>
                      <ScoreBar score={lead.score||0} />
                    </div>
                    {lead.nextFollowUp && (
                      <p style={{ fontSize:'0.65rem', marginTop:'0.3rem', color: new Date(lead.nextFollowUp)<new Date()?'#f87171':'var(--muted)' }}>
                        🔔 {new Date(lead.nextFollowUp).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                      </p>
                    )}
                  </div>
                ))}
                {leads.length === 0 && (
                  <div className="admin-empty is-outlined">No leads</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  ANALYTICS DASHBOARD
// ═══════════════════════════════════════════════════════════════════
export function AdminLeadAnalytics() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [range, setRange]     = useState('all');

  useEffect(() => {
    const params = {};
    if (range === '30') { params.from = new Date(Date.now()-30*86400000).toISOString(); }
    else if (range === '90') { params.from = new Date(Date.now()-90*86400000).toISOString(); }
    api.get('/leads/analytics/summary', { params })
      .then(r => setStats(r.data)).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [range]);

  if (loading) return <div className="admin-empty">Loading analytics...</div>;

  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <p className="tag-label" style={{ marginBottom:'0.3rem' }}>Reports</p>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'1.8rem', color:'var(--cream)' }}>Lead Analytics</h1>
        </div>
        <div style={{ display:'flex', gap:'0.4rem' }}>
          {[['all','All Time'],['90','Last 90 Days'],['30','Last 30 Days']].map(([v,l]) => (
            <button key={v} onClick={() => setRange(v)} className={`btn ${range===v?'btn-gold':'btn-dark'}`} style={{ padding:'0.5rem 1rem', fontSize:'0.72rem' }}>{l}</button>
          ))}
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid-4" style={{ marginBottom:'2rem' }}>
        {[
          { label:'Total Leads', value:stats?.total||0, icon:'📋', color:'var(--gold)' },
          { label:'Hot Leads', value:stats?.hot||0, icon:'🔥', color:'#f87171' },
          { label:'Converted', value:stats?.converted||0, icon:'✅', color:'#4ade80' },
          { label:'Conversion Rate', value:`${stats?.conversionRate||0}%`, icon:'📈', color:'#60a5fa' },
        ].map(k => (
          <div key={k.label} className="card" style={{ padding:'1.5rem', textAlign:'center' }}>
            <div style={{ fontSize:'1.5rem', marginBottom:'0.5rem' }}>{k.icon}</div>
            <div style={{ fontFamily:'var(--font-serif)', fontSize:'2rem', color:k.color, lineHeight:1, marginBottom:'0.3rem' }}>{k.value}</div>
            <div style={{ fontSize:'0.65rem', color:'var(--muted)', textTransform:'uppercase', letterSpacing:'0.12em' }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div className="grid-3" style={{ marginBottom:'1.5rem' }}>
        {/* By Stage */}
        <div className="card" style={{ padding:'1.5rem' }}>
          <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Leads by Stage</h3>
          {stats?.byStage?.map(s => {
            const pct = stats.total>0 ? Math.round((s.count/stats.total)*100) : 0;
            const col = STAGE_COLORS[s._id]||{color:'var(--gold)',bg:'rgba(245,168,0,0.1)'};
            return (
              <div key={s._id} style={{ marginBottom:'0.75rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.3rem' }}>
                  <span style={{ fontSize:'0.75rem', color:'var(--cream-2)' }}>{s._id}</span>
                  <span style={{ fontSize:'0.75rem', color:col.color }}>{s.count}</span>
                </div>
                <div style={{ height:'5px', background:'var(--ink-5)', borderRadius:'3px', overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:col.color, borderRadius:'3px' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* By Source */}
        <div className="card" style={{ padding:'1.5rem' }}>
          <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Leads by Source</h3>
          {stats?.bySource?.sort((a,b)=>b.count-a.count).map((s,i) => {
            const colors = ['#F5A800','#60a5fa','#4ade80','#f87171','#a78bfa','#fb923c','#fbbf24','#22d3ee'];
            const pct = stats.total>0 ? Math.round((s.count/stats.total)*100) : 0;
            return (
              <div key={s._id} style={{ marginBottom:'0.75rem' }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'0.3rem' }}>
                  <span style={{ fontSize:'0.75rem', color:'var(--cream-2)' }}>{s._id||'Unknown'}</span>
                  <span style={{ fontSize:'0.75rem', color:colors[i%colors.length] }}>{s.count} ({pct}%)</span>
                </div>
                <div style={{ height:'5px', background:'var(--ink-5)', borderRadius:'3px', overflow:'hidden' }}>
                  <div style={{ height:'100%', width:`${pct}%`, background:colors[i%colors.length], borderRadius:'3px' }} />
                </div>
              </div>
            );
          })}
        </div>

        {/* Revenue */}
        <div className="card" style={{ padding:'1.5rem' }}>
          <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Revenue Summary</h3>
          <div style={{ textAlign:'center', padding:'1.5rem 0' }}>
            <div style={{ fontFamily:'var(--font-serif)', fontSize:'2rem', color:'var(--gold)', marginBottom:'0.5rem' }}>
              ₹{((stats?.totalProjectValue||0)/100000).toFixed(1)}L
            </div>
            <div style={{ color:'var(--muted)', fontSize:'0.78rem' }}>Total Confirmed Revenue</div>
          </div>
          <div style={{ display:'flex', flexDirection:'column', gap:'0.75rem', marginTop:'1rem' }}>
            {[
              { label:'Confirmed Projects', value:stats?.converted||0, color:'#4ade80' },
              { label:'Lost Leads', value:stats?.lost||0, color:'#f87171' },
              { label:'Avg Lead Score', value:`${stats?.avgScore||0}/100`, color:'var(--gold)' },
            ].map(r => (
              <div key={r.label} style={{ display:'flex', justifyContent:'space-between', padding:'0.6rem 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize:'0.78rem', color:'var(--muted)' }}>{r.label}</span>
                <span style={{ fontSize:'0.82rem', color:r.color, fontWeight:'500' }}>{r.value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Monthly trend */}
      {stats?.byMonth?.length > 0 && (
        <div className="card" style={{ padding:'1.75rem' }}>
          <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1rem', color:'var(--cream)', marginBottom:'1.5rem' }}>Monthly Lead Trend</h3>
          <div style={{ display:'flex', alignItems:'flex-end', gap:'0.75rem', height:'140px', overflowX:'auto' }}>
            {stats.byMonth.map((m, i) => {
              const max = Math.max(...stats.byMonth.map(x=>x.count));
              const h = max > 0 ? (m.count / max) * 120 : 0;
              return (
                <div key={i} style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'0.4rem', flex:1, minWidth:'40px' }}>
                  <span style={{ fontSize:'0.65rem', color:'var(--gold)' }}>{m.count}</span>
                  <div style={{ width:'100%', height:`${h}px`, background:'linear-gradient(to top, var(--gold-dim), var(--gold))', borderRadius:'2px 2px 0 0', minHeight:'4px', transition:'height 0.5s' }} />
                  <span style={{ fontSize:'0.62rem', color:'var(--muted)', whiteSpace:'nowrap' }}>{MONTHS[(m._id?.month||1)-1]} {String(m._id?.year||'').slice(2)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  REMINDERS CENTER
// ═══════════════════════════════════════════════════════════════════
export function AdminReminders() {
  const [reminders, setReminders] = useState([]);
  const [leads, setLeads]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [filter, setFilter]       = useState('pending'); // pending | today | all
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get('/reminders', { params: { isDone: filter === 'pending' || filter === 'today' ? 'false' : undefined } }),
      api.get('/leads/followups/today'),
    ]).then(([remRes, todayRes]) => {
      setReminders(Array.isArray(remRes.data)?remRes.data:[]);
      setLeads(Array.isArray(todayRes.data?.leads)?todayRes.data.leads:[]);
    }).catch(() => toast.error('Failed')).finally(() => setLoading(false));
  }, [filter]);

  useEffect(() => { load(); }, [load]);

  const markDone = async id => {
    try { await api.patch(`/reminders/${id}`, { isDone: true }); load(); toast.success('Marked done ✓'); }
    catch { toast.error('Failed'); }
  };

  const today   = reminders.filter(r => { const d = new Date(r.scheduledAt); const n = new Date(); return d.toDateString()===n.toDateString(); });
  const overdue = reminders.filter(r => new Date(r.scheduledAt) < new Date() && r.scheduledAt && new Date(r.scheduledAt).toDateString()!==new Date().toDateString());
  const upcoming = reminders.filter(r => new Date(r.scheduledAt) > new Date());

  const ReminderCard = ({ rem }) => (
    <div className="card" style={{ padding:'1.1rem', display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'0.6rem' }}>
      <div style={{ flex:1 }}>
        <div style={{ display:'flex', gap:'0.5rem', alignItems:'center', marginBottom:'0.3rem', flexWrap:'wrap' }}>
          <span style={{ fontSize:'0.95rem' }}>{ {'call':'📞','whatsapp':'💬','meeting':'🤝','site_visit':'🏠','follow_up':'🔔'}[rem.type]||'🔔' }</span>
          <button onClick={() => navigate(`/admin/crm/leads/${rem.lead?._id||rem.lead}`)} style={{ fontWeight:'600', color:'var(--cream)', fontSize:'0.85rem', background:'none', cursor:'pointer', textAlign:'left' }}>
            {rem.leadName || rem.lead?.name}
          </button>
          {rem.lead?.priority && <PriorityDot priority={rem.lead.priority} />}
        </div>
        {rem.leadPhone && <a href={`tel:${rem.leadPhone}`} style={{ color:'var(--gold)', fontSize:'0.78rem', display:'block', marginBottom:'0.2rem' }}>{rem.leadPhone}</a>}
        {rem.message && <p style={{ color:'var(--muted)', fontSize:'0.78rem', lineHeight:'1.6' }}>{rem.message}</p>}
        <p style={{ color: new Date(rem.scheduledAt)<new Date()?'#f87171':'var(--gold)', fontSize:'0.72rem', marginTop:'0.3rem' }}>
          📅 {new Date(rem.scheduledAt).toLocaleString('en-IN', { weekday:'short', day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' })}
        </p>
      </div>
      <div style={{ display:'flex', gap:'0.4rem', flexShrink:0, marginLeft:'0.75rem' }}>
        <a href={`https://api.whatsapp.com/send?phone=91${(rem.leadPhone||'').replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="btn btn-dark" style={{ padding:'0.38rem 0.65rem', fontSize:'0.7rem' }}>💬</a>
        <a href={`tel:${rem.leadPhone}`} className="btn btn-dark" style={{ padding:'0.38rem 0.65rem', fontSize:'0.7rem' }}>📞</a>
        <button className="btn btn-gold" style={{ padding:'0.38rem 0.75rem', fontSize:'0.7rem' }} onClick={() => markDone(rem._id)}>✓ Done</button>
      </div>
    </div>
  );

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'1.5rem', flexWrap:'wrap', gap:'1rem' }}>
        <div>
          <p className="tag-label" style={{ marginBottom:'0.3rem' }}>Follow-ups</p>
          <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'1.8rem', color:'var(--cream)' }}>Reminders Center</h1>
        </div>
        <div style={{ display:'flex', gap:'0.5rem' }}>
          {overdue.length > 0 && (
            <span style={{ padding:'0.4rem 0.85rem', background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', color:'#f87171', fontSize:'0.72rem', borderRadius:'2px' }}>
              ⚠ {overdue.length} Overdue
            </span>
          )}
          <span style={{ padding:'0.4rem 0.85rem', background:'rgba(245,168,0,0.1)', border:'1px solid rgba(245,168,0,0.25)', color:'var(--gold)', fontSize:'0.72rem', borderRadius:'2px' }}>
            📅 {today.length} Today
          </span>
        </div>
      </div>

      {loading ? (
        <div className="admin-empty">Loading reminders...</div>
      ) : (
        <div className="grid-2" style={{ gap:'2rem' }}>
          <div>
            {overdue.length > 0 && (
              <>
                <h3 style={{ fontSize:'0.8rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'#f87171', marginBottom:'0.75rem', display:'flex', alignItems:'center', gap:'0.5rem' }}>⚠ Overdue ({overdue.length})</h3>
                {overdue.map(r => <ReminderCard key={r._id} rem={r} />)}
              </>
            )}
            {today.length > 0 && (
              <>
                <h3 style={{ fontSize:'0.8rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--gold)', marginBottom:'0.75rem', marginTop: overdue.length?'1.5rem':'0', display:'flex', alignItems:'center', gap:'0.5rem' }}>📅 Today ({today.length})</h3>
                {today.map(r => <ReminderCard key={r._id} rem={r} />)}
              </>
            )}
            {upcoming.length > 0 && (
              <>
                <h3 style={{ fontSize:'0.8rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.75rem', marginTop:'1.5rem' }}>🗓 Upcoming ({upcoming.length})</h3>
                {upcoming.map(r => <ReminderCard key={r._id} rem={r} />)}
              </>
            )}
            {reminders.length === 0 && (
              <div style={{ textAlign:'center', padding:'4rem', border:'1px dashed var(--ink-5)' }}>
                <div style={{ fontSize:'3rem', marginBottom:'1rem' }}>✅</div>
                <p style={{ color:'var(--muted)' }}>All caught up! No pending reminders.</p>
              </div>
            )}
          </div>
          {/* Today's follow-up leads */}
          <div>
            <h3 style={{ fontSize:'0.8rem', letterSpacing:'0.15em', textTransform:'uppercase', color:'var(--muted)', marginBottom:'0.75rem' }}>Today's Follow-up Leads ({leads.length})</h3>
            {leads.length === 0 ? (
              <div className="admin-empty is-outlined">No follow-up leads scheduled for today.</div>
            ) : (
              leads.map(lead => (
                <div key={lead._id} className="card" style={{ padding:'1.1rem', marginBottom:'0.6rem' }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ display:'flex', gap:'0.5rem', alignItems:'center' }}>
                        <PriorityDot priority={lead.priority} />
                        <span style={{ fontWeight:'500', color:'var(--cream)', fontSize:'0.85rem' }}>{lead.name}</span>
                        <StageBadge stage={lead.stage} />
                      </div>
                      <a href={`tel:${lead.phone}`} style={{ color:'var(--gold)', fontSize:'0.78rem', display:'block', marginTop:'0.25rem' }}>{lead.phone}</a>
                      {lead.reminderNote && <p style={{ color:'var(--muted)', fontSize:'0.75rem', marginTop:'0.2rem' }}>{lead.reminderNote}</p>}
                    </div>
                    <button onClick={() => navigate(`/admin/crm/leads/${lead._id}`)} className="btn btn-dark" style={{ padding:'0.4rem 0.8rem', fontSize:'0.7rem' }}>View →</button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
//  QUOTATION BUILDER
// ═══════════════════════════════════════════════════════════════════
export function AdminQuotationBuilder() {
  const { leadId, id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    items: [{ description:'', area:'', unit:'sq ft', rate:'', qty:1, amount:0 }],
    gst: 18, discount: 0, terms: 'This quotation is valid for 30 days from the date of issue. Payment: 5% on design sign-off, 50% on production dispatch, 45% on project completion.', notes: '', validUntil: ''
  });

  useEffect(() => {
    if (leadId) api.get(`/leads/${leadId}`).then(r => setLead(r.data.lead)).catch(() => {});
    if (id) api.get(`/quotations/${id}`).then(r => { const q = r.data; setLead(q.lead); setForm({ items: q.items, gst: q.gst, discount: q.discount, terms: q.terms, notes: q.notes, validUntil: q.validUntil?.split('T')[0]||'' }); }).catch(() => {});
  }, [leadId, id]);

  const updateItem = (i, key, val) => {
    const items = [...form.items];
    items[i] = { ...items[i], [key]: val };
    if (key === 'rate' || key === 'qty') items[i].amount = parseFloat(items[i].rate||0) * parseFloat(items[i].qty||1);
    setForm(p => ({ ...p, items }));
  };

  const addItem = () => setForm(p => ({ ...p, items: [...p.items, { description:'', area:'', unit:'sq ft', rate:'', qty:1, amount:0 }] }));
  const removeItem = i => setForm(p => ({ ...p, items: p.items.filter((_,idx)=>idx!==i) }));

  const subtotal = form.items.reduce((s,i) => s + parseFloat(i.amount||0), 0);
  const gstAmt   = Math.round(subtotal * form.gst / 100);
  const total    = subtotal + gstAmt - parseFloat(form.discount||0);

  const submit = async () => {
    setSaving(true);
    try {
      if (id) {
        await api.patch(`/quotations/${id}`, { ...form, subtotal, gstAmount: gstAmt, total });
        toast.success('Quotation updated!');
      } else {
        const q = await api.post('/quotations', { ...form, lead: leadId||lead?._id, leadName: lead?.name, subtotal, gstAmount: gstAmt, total });
        toast.success(`Quotation ${q.data.quotationNo} created!`);
        navigate(`/admin/crm/quotations/${q.data._id}`);
      }
    } catch { toast.error('Failed to save'); } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={{ marginBottom:'1.5rem' }}>
        <button onClick={() => navigate(-1)} style={{ color:'var(--muted)', fontSize:'0.78rem', background:'none', cursor:'pointer', marginBottom:'0.5rem' }}>← Back</button>
        <h1 style={{ fontFamily:'var(--font-serif)', fontSize:'1.8rem', color:'var(--cream)' }}>{id ? 'Edit' : 'New'} Quotation</h1>
        {lead && <p style={{ color:'var(--muted)', marginTop:'0.3rem' }}>For: <span style={{ color:'var(--gold)' }}>{lead.name}</span> — {lead.phone}</p>}
      </div>

      <div className="crm-detail-grid" style={{ gap:'1.5rem' }}>
        {/* Line items */}
        <div>
          <div className="card" style={{ padding:'1.5rem', marginBottom:'1rem' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'1rem' }}>
              <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1.1rem', color:'var(--cream)' }}>Line Items</h3>
              <button className="btn btn-gold" style={{ padding:'0.4rem 0.9rem', fontSize:'0.7rem' }} onClick={addItem}>+ Add Item</button>
            </div>
            <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', minWidth:'620px', fontSize:'0.8rem', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ borderBottom:'1px solid var(--ink-5)' }}>
                  {['Description','Area','Unit','Qty','Rate (₹)','Amount (₹)',''].map(h => (
                    <th key={h} style={{ padding:'0.5rem 0.4rem', textAlign:'left', fontSize:'0.65rem', color:'var(--muted)', letterSpacing:'0.1em', textTransform:'uppercase', fontWeight:'500' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {form.items.map((item, i) => (
                  <tr key={i} style={{ borderBottom:'1px solid rgba(255,255,255,0.04)' }}>
                    {[
                      <input value={item.description} onChange={e=>updateItem(i,'description',e.target.value)} placeholder="e.g. Modular Kitchen with granite top" style={{ width:'100%', background:'transparent', border:'none', borderBottom:'1px solid var(--ink-5)', color:'var(--cream)', padding:'0.5rem 0', fontSize:'0.8rem' }} />,
                      <input value={item.area} onChange={e=>updateItem(i,'area',e.target.value)} placeholder="120" style={{ width:'70px', background:'transparent', border:'none', borderBottom:'1px solid var(--ink-5)', color:'var(--cream)', padding:'0.5rem 0', fontSize:'0.8rem' }} />,
                      <select value={item.unit} onChange={e=>updateItem(i,'unit',e.target.value)} style={{ background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', color:'var(--cream)', padding:'0.4rem', fontSize:'0.75rem' }}>
                        {['sq ft','rft','nos','lumpsum'].map(u=><option key={u} value={u}>{u}</option>)}
                      </select>,
                      <input type="number" value={item.qty} onChange={e=>updateItem(i,'qty',e.target.value)} style={{ width:'50px', background:'transparent', border:'none', borderBottom:'1px solid var(--ink-5)', color:'var(--cream)', padding:'0.5rem 0', fontSize:'0.8rem' }} />,
                      <input type="number" value={item.rate} onChange={e=>updateItem(i,'rate',e.target.value)} placeholder="850" style={{ width:'80px', background:'transparent', border:'none', borderBottom:'1px solid var(--ink-5)', color:'var(--cream)', padding:'0.5rem 0', fontSize:'0.8rem' }} />,
                      <span style={{ color:'var(--gold)', fontWeight:'500' }}>₹{parseFloat(item.amount||0).toLocaleString('en-IN')}</span>,
                      <button onClick={()=>removeItem(i)} style={{ color:'#f87171', background:'none', cursor:'pointer', fontSize:'1rem' }}>✕</button>
                    ].map((cell,j)=><td key={j} style={{ padding:'0.6rem 0.4rem' }}>{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>

          <div className="card" style={{ padding:'1.5rem' }}>
            <div className="form-group">
              <label>Terms & Conditions</label>
              <textarea value={form.terms} onChange={e=>setForm(p=>({...p,terms:e.target.value}))} rows={4} style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem', resize:'vertical' }} />
            </div>
            <div className="form-group">
              <label>Notes for Client</label>
              <textarea value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2} placeholder="Any additional notes..." style={{ width:'100%', background:'var(--ink-3)', border:'1px solid var(--ink-5)', borderRadius:'var(--radius)', borderBottom:'1px solid var(--gold-dim)', color:'var(--cream)', padding:'0.9rem 1.1rem', resize:'vertical' }} />
            </div>
          </div>
        </div>

        {/* Summary sidebar */}
        <div>
          <div className="card" style={{ padding:'1.5rem', marginBottom:'1rem' }}>
            <h3 style={{ fontFamily:'var(--font-serif)', fontSize:'1rem', color:'var(--cream)', marginBottom:'1.25rem' }}>Quote Summary</h3>
            {[
              ['Subtotal', `₹${subtotal.toLocaleString('en-IN')}`],
              [`GST (${form.gst}%)`, `₹${gstAmt.toLocaleString('en-IN')}`],
              ['Discount', `₹${parseFloat(form.discount||0).toLocaleString('en-IN')}`],
            ].map(([l,v]) => (
              <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'0.5rem 0', borderBottom:'1px solid rgba(255,255,255,0.05)' }}>
                <span style={{ fontSize:'0.8rem', color:'var(--muted)' }}>{l}</span>
                <span style={{ fontSize:'0.8rem', color:'var(--cream-2)' }}>{v}</span>
              </div>
            ))}
            <div style={{ display:'flex', justifyContent:'space-between', padding:'0.75rem 0', borderTop:'1px solid var(--gold-dim)', marginTop:'0.25rem' }}>
              <span style={{ fontWeight:'600', color:'var(--cream)' }}>Total</span>
              <span style={{ fontFamily:'var(--font-serif)', fontSize:'1.25rem', color:'var(--gold)', fontWeight:'600' }}>₹{total.toLocaleString('en-IN')}</span>
            </div>
            <div style={{ marginTop:'1rem', display:'flex', flexDirection:'column', gap:'0.75rem' }}>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>GST %</label>
                <input type="number" value={form.gst} onChange={e=>setForm(p=>({...p,gst:parseFloat(e.target.value)||0}))} />
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Discount (₹)</label>
                <input type="number" value={form.discount} onChange={e=>setForm(p=>({...p,discount:parseFloat(e.target.value)||0}))} />
              </div>
              <div className="form-group" style={{ marginBottom:0 }}>
                <label>Valid Until</label>
                <input type="date" value={form.validUntil} onChange={e=>setForm(p=>({...p,validUntil:e.target.value}))} />
              </div>
            </div>
          </div>
          <button className="btn btn-gold" style={{ width:'100%', justifyContent:'center', marginBottom:'0.75rem' }} onClick={submit} disabled={saving}>
            {saving ? 'Saving...' : id ? '✦ Update Quotation' : '✦ Create Quotation'}
          </button>
        </div>
      </div>
    </div>
  );
}
